import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PreviewPlayer from '@/pages/detail/components/PreviewPlayer';
import CourseCard from '@/components/base/CourseCard';
import { formatCount, getCourseById, getRelatedCourses } from '@/utils/course';
import { useFavorites } from '@/hooks/useFavorites';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const course = id ? getCourseById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  if (!course) {
    return (
      <div className="w-full px-4 md:px-8 py-20 flex flex-col items-center justify-center text-center">
        <span className="w-20 h-20 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-4xl mb-5">
          <i className="ri-error-warning-line"></i>
        </span>
        <h1 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-2">
          该课程不存在或已下架
        </h1>
        <p className="text-sm text-foreground-600 mb-6">
          你访问的课程可能已被移除，去看看其他优质课程吧。
        </p>
        <div className="flex items-center gap-3">
          <Link
            to="/category"
            className="h-11 px-6 inline-flex items-center rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回课程分类
          </Link>
          <Link
            to="/"
            className="h-11 px-6 inline-flex items-center rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            回到首页
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedCourses(course, 3);
  const favorited = isFavorite(course.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast('链接已复制到剪贴板');
    } catch {
      setToast('复制失败，请手动复制浏览器地址栏链接');
    }
    window.setTimeout(() => setToast(''), 2200);
  };

  const metaItems = [
    { icon: 'ri-calendar-line', label: '发布日期', value: course.publishedAt },
    { icon: 'ri-time-line', label: '课程时长', value: course.duration },
    { icon: 'ri-signal-tower-line', label: '难度等级', value: course.level },
    { icon: 'ri-price-tag-3-line', label: '课程类别', value: `${course.categoryName} · ${course.subcategory}` },
  ];

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-5">
        <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
        <span>/</span>
        <Link to="/category" className="hover:text-primary-600 cursor-pointer">课程分类</Link>
        <span>/</span>
        <Link to={`/category?cat=${course.category}`} className="hover:text-primary-600 cursor-pointer">
          {course.categoryName}
        </Link>
        <span>/</span>
        <span className="text-foreground-800 line-clamp-1">{course.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 md:gap-8 items-start">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-secondary-100 text-secondary-900">
              {course.categoryName}
            </span>
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-50 text-primary-700">
              {course.subcategory}
            </span>
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-accent-100 text-accent-800">
              {course.level}
            </span>
          </div>

          <h1 className="font-heading font-extrabold text-2xl md:text-3xl lg:text-4xl text-foreground-950 leading-tight mb-4">
            {course.title}
          </h1>

          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
            {course.intro}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {metaItems.map((m) => (
              <div key={m.label} className="rounded-lg border border-background-200 bg-background-50 px-4 py-3">
                <div className="flex items-center gap-1.5 text-foreground-500 text-xs mb-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={m.icon}></i>
                  </span>
                  {m.label}
                </div>
                <div className="font-medium text-sm text-foreground-900">{m.value}</div>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden mb-6">
            <h2 className="font-heading font-bold text-base text-foreground-950 px-4 py-3 border-b border-background-200 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-primary-500 text-lg">
                <i className="ri-list-check-2"></i>
              </span>
              课程教学大纲
            </h2>
            <ul className="divide-y divide-background-200">
              {course.outline.map((item, i) => (
                <li key={item} className="flex items-start gap-3 px-4 py-3.5">
                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 text-foreground-700 text-xs font-bold shrink-0">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-sm text-foreground-800 leading-relaxed pt-1">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <PreviewPlayer course={course} />
        </div>

        <aside className="lg:sticky lg:top-24">
          <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
            <div className="relative w-full aspect-[8/5] overflow-hidden bg-background-200">
              <img
                src={course.cover}
                alt={course.title}
                title={`${course.title} · 秘密教学课程`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-5">
              <div className="flex items-end gap-2 mb-4">
                {course.priceType === '免费' ? (
                  <span className="font-heading font-extrabold text-3xl text-secondary-600">免费</span>
                ) : (
                  <>
                    <span className="font-heading font-extrabold text-3xl text-accent-600">¥{course.price}</span>
                    <span className="text-xs text-foreground-500 mb-1">限时特惠</span>
                  </>
                )}
              </div>

              <button
                type="button"
                className="w-full h-11 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap mb-3"
              >
                立即开始学习
              </button>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(course.id)}
                  className={`h-10 rounded-md text-sm font-medium flex items-center justify-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? 'bg-accent-100 text-accent-800'
                      : 'border border-background-300 text-foreground-800 hover:border-accent-300'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
                  </span>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="h-10 rounded-md border border-background-300 text-foreground-800 text-sm font-medium flex items-center justify-center gap-1.5 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-share-line"></i>
                  </span>
                  分享
                </button>
              </div>

              <div className="flex items-center justify-between mt-5 pt-4 border-t border-background-200 text-xs text-foreground-600">
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-3-line"></i></span>
                  {formatCount(course.students)} 人在学
                </span>
                <span className="flex items-center gap-1 text-accent-600 font-medium">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></span>
                  {course.rating.toFixed(1)} 分
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 mt-4">
            <h3 className="font-heading font-bold text-sm text-foreground-950 mb-3">授课讲师</h3>
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 rounded-full overflow-hidden bg-background-200 shrink-0">
                <img src={course.teacherAvatar} alt={course.teacher} className="w-full h-full object-cover" />
              </span>
              <div>
                <div className="font-heading font-bold text-sm text-foreground-900">{course.teacher}</div>
                <div className="text-xs text-foreground-500">{course.teacherTitle}</div>
              </div>
            </div>
          </div>

          <Link
            to={`/category?cat=${course.category}`}
            className="mt-4 h-11 rounded-md border border-background-300 text-foreground-800 text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </span>
            返回课程分类
          </Link>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-12 md:mt-16">
          <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-5">
            相关推荐课程
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {related.map((c) => (
              <CourseCard key={c.id} course={c} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-md bg-primary-900 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}